import {
    Alert,
    Box,
    Button,
    IconButton,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { saveBlobAsFile } from '../../../../api/apiUtils.js'
import {
    createRuleSetting,
    deleteRuleSetting,
    downloadRuleSetting,
    getRuleConditions,
    searchRuleSetting,
    updateRuleSetting,
} from '../../../../api/RuleSettingAPI'
import {
    ColumnConfigurationIcon,
    DownloadIcon,
    SearchIconBtn,
} from '../../../../assets/icons'
import { getValidCriteria } from '../../../../constants'
import { sanitizeForLog } from '../../../../constants/index.js'
import { normalizeDate } from '../../../../constants/Utils.js'
import { useSnackbar } from '../../../../contexts/SnackbarProvider.jsx'
import {
    CommonModal,
    CommonTable,
    ConfirmationDialog,
    Search,
} from '../../../common'
import ColumnConfiguration from '../../../common/columnConfiguration.jsx'
import KeyFilter from '../common/KeyFilter.jsx'
import {
    columns,
    fieldOptions,
    searchFields,
} from '../configs/ruleSettingTableConfig.jsx'
import AddRuleSetting from './AddRuleSetting'

const RuleSetting = ({ roleConfig }) => {
  const [rules, setRules] = useState([])
  const [showTable, setShowTable] = useState(false)
  const [showAddRuleSetting, setShowAddRuleSetting] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [keyFilterData, setKeyFilterData] = useState([])
  const [ruleSettingForm, setRuleSettingForm] = useState({
    id: null,
    ruleName: '',
    mappedTdsSection: '',
    startDate: '',
    endDate: '',
    active: true,
    createdBy: '',
    createdAt: '',
    updatedBy: '',
    updatedAt: '',
    conditions: [],
  })
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)
  const [formErrors, setFormErrors] = useState({})
  const [formLoading, setFormLoading] = useState(false)
  const [tanMasterId, setTanMasterId] = useState(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState(null)
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map(col => col.id)
  )
  const filteredColumns = useMemo(
    () => columns.filter(col => visibleColumns.includes(col.id)),
    [visibleColumns]
  )
  const [selectedRows, setSelectedRows] = useState([])

  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    if (tanMasterId) {
      // Reset pagination
      setCurrentPage(1)

      // Reset search filters to default
      setSearchCriteria([
        {
          id: 1,
          logicalOperator: 'Where',
          field: searchFields[0]?.id || '',
          value: '',
        },
      ])

      // Clear errors
      setFormErrors({})

      // Reset rule form
      setRuleSettingForm({
        id: null,
        ruleName: '',
        mappedTdsSection: '',
        startDate: '',
        endDate: '',
        active: true,
        status: '',
        createdBy: '',
        createdAt: '',
        updatedBy: '',
        updatedAt: '',
        conditions: [],
      })
    }
  }, [tanMasterId])

  const formatDateForApi = dateString => {
    if (!dateString) return ''
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/')
      return `${year}-${month}-${day}`
    }
    if (dateString.includes('-')) return dateString // already YYYY-MM-DD
    return ''
  }

  const loadRuleSettingData = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    isSearch = false
  ) => {
    setLoading(true)
    try {
      // Format startDate and endDate in searchList
      const formattedSearchList = searchList.map(item => {
        if (item.field === 'startDate' || item.field === 'endDate') {
          return { ...item, value: formatDateForApi(item.value) }
        }
        return item
      })
      // Add filterOperator to keyFilterData only during search operations
      const enhancedKeyFilterData = isSearch
        ? keyFilterData.map(filter => ({ ...filter, logicalOperator: 'AND' }))
        : []

      // get API
      const payload = isSearch
        ? {
            pageFilter: {
              currentPage: page,
              includeCount: true,
              pageSize: pageSize,
              sortField: 'lastModifiedDate',
              sortOrder: 'ASC',
            },
            userRoleType: 'ADMIN',
            searchCoList: [...enhancedKeyFilterData, ...formattedSearchList],
          }
        : {
            pageFilter: {
              currentPage: page,
              includeCount: true,
              pageSize: pageSize,
              sortField: 'lastModifiedDate',
              sortOrder: 'ASC',
            },
            userRoleType: 'ADMIN',
            searchCoList: [...searchList],
          }
      let resp = await searchRuleSetting(payload)
      if (
        resp.success &&
        resp.data &&
        resp.data.data &&
        Array.isArray(resp.data.data.content)
      ) {
        const rowsWithId = (resp.data.data.content || []).map((row, idx) => ({
          ...row,
          startDate: normalizeDate(row.startDate),
          endDate: normalizeDate(row.endDate),
          createdAt: row.createdDate,
          updatedAt: row.lastModifiedDate,
          updatedBy: row.lastModifiedBy,
          status:
            row.active === true || row.active === 'true'
              ? 'Active'
              : 'Inactive',
          // Use nullish coalescing and avoid 0 being treated as falsy; default to idx+1
          id: row.id ?? row.uuid ?? idx + 1,
        }))
        setRules([...rowsWithId])
        setTotalItems(resp.data.data.totalElements)
        setShowTable(true)
      } else if (!resp.success) {
        setLoading(false)
        setNotification({
          open: true,
          message: resp.error || 'Failed to fetch Rule Setting',
          severity: 'error',
        })
      }
    } catch (error) {
      console.error('Failed to load Rule Setting:')
    } finally {
      setLoading(false)
    }
  }

  const handleViewClick = data => {
    const searchList = [
      {
        fieldName: 'tanNumber',
        operator: 'EQUAL_TO',
        value: data.tan,
      },
    ]
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setKeyFilterData(searchList)
    setTanMasterId(data.tanMasterUuid)
    setShowTable(true)

    // Only call loadRuleSettingData after state updates are complete
    setTimeout(() => {
      loadRuleSettingData(1, itemsPerPage, searchList, false)
    }, 0)
  }

  const handleAddRuleCancel = () => {
    setShowAddRuleSetting(false)
    setRuleSettingForm({
      id: null,
      ruleName: '',
      mappedTdsSection: '',
      startDate: '',
      endDate: '',
      status: '',
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
    })
    setFormErrors({})
  }

  const handleEdit = useCallback(async rowData => {
    try {
      const res = await getRuleConditions(rowData.ruleId)
      let mappedConditions = []
      if (res?.success && res?.data?.data) {
        const apiConditions = res.data.data || []
        mappedConditions = apiConditions.map((c, idx) => {
          const apiFieldCode = (c.conditionName || c.field || '').toUpperCase()
          // Map API code to label expected by AddRuleSetting field Select
          const fieldLabel =
            fieldOptions.find(f => f.value === apiFieldCode)?.value ||
            apiFieldCode
          const operatorValue = (
            c.operatorName ||
            c.operator ||
            ''
          ).toUpperCase()
          // The API stores the AND/OR join on the condition that precedes it.
          // The UI shows the join on the row it applies to (condition N+1),
          // so shift the previous condition's operator onto this row.
          let logicalOperator = 'Where'
          if (idx > 0) {
            const prevJoin = (
              apiConditions[idx - 1].logicalOperator ||
              apiConditions[idx - 1].filterOperator ||
              ''
            ).toUpperCase()
            logicalOperator = prevJoin === 'OR' ? 'OR' : 'And'
          }
          return {
            id: c.id,
            field: fieldLabel,
            operator: operatorValue,
            value: c.conditionDescription || c.value || '',
            logicalOperator,
          }
        })
      }
      setRuleSettingForm({
        id: (rowData.id ?? rowData.ruleId ?? rowData.uuid ?? '').toString(),
        ruleId: rowData.ruleId, // preserve backend rule id for delete after update
        ruleName: rowData.ruleName || '',
        mappedTdsSection: rowData.mappedTdsSection || '',
        startDate: rowData.startDate || '',
        endDate: rowData.endDate || '',
        active: rowData.active === true || rowData.active === 'true',
        status: rowData.status || '',
        createdBy: rowData.createdBy || '',
        createdAt: rowData.createdAt || '',
        updatedBy: rowData.updatedBy || '',
        updatedAt: rowData.updatedAt || '',
        conditions: mappedConditions,
      })
      setFormErrors({})
      setShowAddRuleSetting(true)
    } catch (e) {
      setNotification({
        open: true,
        message: 'Failed to load rule conditions',
        severity: 'error',
      })
    }
  }, [])

  const handleAddDialog = () => {
    setRuleSettingForm({
      id: null,
      ruleName: '',
      mappedTdsSection: '',
      startDate: '',
      endDate: '',
      status: '',
      active: true,
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
    })
    setFormErrors({})
    setShowAddRuleSetting(true)
  }

  const handleAddRuleSave = (skipReload = false) => {
    if (!skipReload) {
      loadRuleSettingData(currentPage, itemsPerPage, keyFilterData)
    }
    setShowAddRuleSetting(false)
  }

  const handleSubmitRuleSetting = async ({ formData, conditions }) => {
    const isEditMode =
      formData?.id !== null &&
      formData?.id !== undefined &&
      String(formData?.id).trim() !== ''
    const newErrors = {}
    const trimOrEmpty = v => (typeof v === 'string' ? v.trim() : v)
    if (!trimOrEmpty(formData?.ruleName))
      newErrors.ruleName = 'Rule Name is required'
    if (!formData?.tdsSection && !isEditMode)
      newErrors.tdsSection = 'TDS Section is required'
    if (!formData?.startDate) newErrors.startDate = 'Start Date is required'
    if (formData?.startDate && formData?.endDate) {
      const [sd, sm, sy] = formData.startDate.split('/')
      const [ed, em, ey] = formData.endDate.split('/')
      const start = new Date(`${sy}-${sm}-${sd}`)
      const end = new Date(`${ey}-${em}-${ed}`)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (start > end) {
        newErrors.endDate = 'End date must be after start date'
      } else if (start === end) {
        newErrors.endDate = 'End date cannot be the same as start date'
      } else if (formData.active && end <= today) {
        newErrors.endDate =
          'For Active rules, End date must be greater than today'
      }
    }
    ;(conditions || []).forEach(c => {
      if (!c.field) newErrors[`condition_${c.id}_field`] = 'Field is required'
      if (!c.operator)
        newErrors[`condition_${c.id}_operator`] = 'Operator is required'
      // Disable value validation for IS_NULL/IS_NOT_NULL
      const op = String(c.operator).toUpperCase()
      const field = String(c.field).toUpperCase()
      if (op === 'IS_NULL' || op === 'IS_NOT_NULL') {
        // Do not check value
        return
      }
      // For Entity TAN/Vendor PAN, skip PAN/TAN validation for operators other than allowed
      if (
        (field === 'ENTITY TAN' || field === 'VENDOR PAN') &&
        !['IS_NULL', 'IS_NOT_NULL', 'EQUAL_TO', 'NOT_EQUAL_TO'].includes(op)
      ) {
        // Do not check PAN/TAN validation (assume handled in AddRuleSetting)
        return
      }
      if (!trimOrEmpty(c.value))
        newErrors[`condition_${c.id}_value`] = 'Value is required'
    })
    if (Object.keys(newErrors).length) {
      setFormErrors(newErrors)
      setNotification({
        open: true,
        message: 'Please fix validation errors',
        severity: 'error',
      })
      return { success: false }
    }

    const toApiFormat = str =>
      str ? str.toUpperCase().replace(/\s+/g, '_') : ''
    const formatDateForApi = dateString => {
      if (!dateString) return ''
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/')
        return `${year}-${month}-${day}`
      }
      if (dateString.includes('-')) return dateString // already YYYY-MM-DD
      return ''
    }

    const formattedConditions = (conditions || []).map((c, idx, arr) => {
      const op = String(c.operator).toUpperCase()
      let value = c.value
      if (op === 'IS_NULL' || op === 'IS_NOT_NULL') value = ''
      const obj = {
        conditionName: c.field,
        operatorName: toApiFormat(c.operator),
        conditionDescription: value,
      }
      // Add filterOperator from the next condition's logicalOperator
      if (idx < arr.length - 1) {
        const nextLogical = arr[idx + 1].logicalOperator
        if (nextLogical) {
          obj.logicalOperator = nextLogical.toUpperCase()
        }
      }
      if (isEditMode && c.id && typeof c.id === 'string') {
        obj.conditionId = c.id
      }
      return obj
    })

    const payload = {
      ruleName: formData.ruleName,
      startDate: formatDateForApi(formData.startDate),
      endDate: formatDateForApi(formData.endDate),
      active: formData.active || false,
      sectionMasterId:
        formData.tdsSection ||
        formData.sectionMasterId ||
        formData.sectionId ||
        null,
      tanMasterId: tanMasterId,
      conditions: formattedConditions,
    }

    if (isEditMode) {
      payload.ruleId = formData.ruleId
    }

    try {
      const resp = await (isEditMode
        ? updateRuleSetting(payload)
        : createRuleSetting(payload))
      if (resp?.success) {
        if (isEditMode && resp?.data?.data === 'Rule updated successfully') {
          showSnackbar('Rule setting updated successfully!', 'success')
          handleAddRuleSave()
          return { success: true }
        } else if (!isEditMode) {
          // New rule created
          showSnackbar('Rule setting created successfully!', 'success')
          handleAddRuleSave()
          return { success: true }
        } else {
          showSnackbar(
            resp?.error ||
              resp?.data?.data ||
              'Failed to create/update rule setting',
            'error'
          )
        }
      } else {
        showSnackbar(
          resp?.error ||
            resp?.data?.data ||
            'Failed to create/update rule setting',
          'error'
        )
      }
      return { success: false }
    } catch (e) {
      showSnackbar(e?.message || 'An unexpected error occurred', 'error')
      return { success: false }
    }
  }

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)

    // Format startDate and endDate before sending
    const formattedSearchList = searchList.map(item => {
      const fieldName = item.fieldName || item.field || ''
      if (fieldName === 'startDate' || fieldName === 'endDate') {
        return { ...item, value: formatDateForApi(item.value) }
      }
      // Handle active/status field - convert to boolean for API
      if (fieldName === 'active' || fieldName === 'status') {
        const lowerValue = (item.value || '').toString().toLowerCase()
        return { ...item, value: lowerValue === 'active' }
      }
      return item
    })
    await loadRuleSettingData(1, itemsPerPage, formattedSearchList, true)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadRuleSettingData(1, itemsPerPage, keyFilterData)
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const handleDelete = useCallback(rule => {
    setRuleToDelete(rule)
    setDeleteDialogOpen(true)
  }, [])

  const confirmDelete = async () => {
    if (ruleToDelete) {
      try {
        const response = await deleteRuleSetting(ruleToDelete.ruleId)
        if (response.success) {
          showSnackbar(
            response?.data?.data || 'Rule deleted successfully',
            'success'
          )
          setDeleteDialogOpen(false)
          setRuleToDelete(null)
          loadRuleSettingData(1, itemsPerPage, keyFilterData, false)
        } else {
          showSnackbar(response?.error, 'error')
        }
      } catch (error) {
        showSnackbar('Failed to delete rule', 'error')
      }
    }
  }

  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    setStartDownloading(true)
    try {
      const payload = {
        pageFilter: { currentPage: 1, includeCount: true, pageSize: 100 },
        ...(selectedRows.length > 0 && { ruleIds: selectedRows }),
        userRoleType: 'ADMIN',
        isReportDownload: true,
        searchCoList: keyFilterData,
      }

      const blob = await downloadRuleSetting(payload)
      saveBlobAsFile(blob, 'rule_settings.xlsx') // ✅ secure helper
    } catch (error) {
      const safeMsg = sanitizeText(error.message)
      console.error(
        'Failed to download Rule Settings Excel:',
        sanitizeForLog(error)
      )
      setNotification({
        open: true,
        message: safeMsg || 'Download failed',
        severity: 'error',
      })
    } finally {
      setStartDownloading(false)
    }
  }
  // Keep latest values in refs so the pagination handlers passed to the
  // memoized CommonTable can stay referentially stable (never re-created),
  // preventing the large table from re-rendering when the Search popper state
  // changes in this same parent.
  const paginationStateRef = useRef({
    itemsPerPage,
    searchCriteria,
    keyFilterData,
  })
  const loadRuleSettingDataRef = useRef(loadRuleSettingData)
  useEffect(() => {
    paginationStateRef.current = { itemsPerPage, searchCriteria, keyFilterData }
    loadRuleSettingDataRef.current = loadRuleSettingData
  })

  const handlePageChange = useCallback(page => {
    const { itemsPerPage: ipp, searchCriteria: sc } = paginationStateRef.current
    setCurrentPage(page)
    loadRuleSettingDataRef.current(page, ipp, getValidCriteria(sc))
  }, [])

  const handleItemsPerPageChange = useCallback(newItemsPerPage => {
    const { searchCriteria: sc } = paginationStateRef.current
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadRuleSettingDataRef.current(1, newItemsPerPage, getValidCriteria(sc))
  }, [])

  const handleChangeForm = updatedData => {
    setRuleSettingForm(prev => ({
      ...prev,
      ...updatedData,
    }))
    // Clear errors for the field that was just updated
    if (formErrors) {
      const newErrors = { ...formErrors }
      Object.keys(updatedData).forEach(id => {
        if (newErrors[id]) {
          delete newErrors[id]
        }
      })
      setFormErrors(newErrors)
    }
  }

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }))
  }

  const handleColumnConfigOpen = event => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(!columnConfigOpen)
  }

  const handleColumnChange = selectedColumns => {
    setVisibleColumns(selectedColumns)
  }

  const handleColumnConfigurationClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleSelectRow = uuid => {
    // CWE-117: sanitize untrusted identifier before logging (strip CR/LF).
    console.log(sanitizeForLog(uuid), 'uuid')

    setSelectedRows(prev =>
      prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
    )
  }

  const handleSelectAll = e => {
    setSelectedRows(e.target.checked ? rules.map(r => r.ruleId) : [])
  }

  return (
    <Box>
      {/* Filter Section */}
      <KeyFilter handleViewClick={handleViewClick} />

      {/* Table Section */}
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '32px',
              py: '12px',
            }}
          >
            {/* Left side - Title */}
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              Rule Setting
            </Typography>

            {/* Right side - Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Column Configuration">
                <IconButton
                  sx={{
                    color: 'var(--icon-orange)',
                  }}
                  onClick={handleColumnConfigOpen}
                >
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Excel">
                <span className="disabled:cursor-not-allowed hover:bg-[#e3f2fd] rounded-full">
                  <IconButton
                    onClick={handleDownload}
                    disabled={loading || startDownloading}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Search">
                <IconButton onClick={handleSearch}>
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>

              {roleConfig.canCreate && (
                <>
                  <Typography
                    sx={{
                      height: '16px',
                      borderLeft: '1px solid var(--forbrand-color)',
                      marginRight: '6px',
                    }}
                  ></Typography>

                  <Button
                    variant="contained"
                    onClick={handleAddDialog}
                    sx={{
                      backgroundColor: 'var(--forbrand-bg-variant)',
                      color: 'var(--forbrand-brand-varient)',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '16px',
                    }}
                  >
                    Add
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <CommonTable
              columns={filteredColumns}
              data={rules}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              emptyMessage="No Rule Settings found for the selected Entity and TAN."
              showFooter={true}
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
              showPageNumbers={true}
              showActions={true}
              showCheckbox={true}
              selectedItems={selectedRows}
              onSelectItem={handleSelectRow}
              onSelectAll={handleSelectAll}
            />
          </Box>
        </>
      )}
      {/* Add Section Dialog */}
      <CommonModal
        open={showAddRuleSetting}
        onClose={handleAddRuleCancel}
        showCloseButton={true}
        fullScreen={true}
        maxWidth={'lg'}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0,
          },
          '& .MuiDialogTitle-root': {
            display: 'none',
          },
        }}
      >
        <AddRuleSetting
          open={showAddRuleSetting}
          onCancel={handleAddRuleCancel}
          formData={ruleSettingForm}
          formErrors={formErrors}
          formLoading={formLoading}
          onClose={handleAddRuleCancel}
          onSave={handleAddRuleSave}
          onChange={handleChangeForm}
          tanMasterId={tanMasterId}
          onNotify={({ message, severity = 'info' }) =>
            setNotification({ open: true, message, severity })
          }
          onSubmit={handleSubmitRuleSetting}
        />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this rule?"
        message={`Deleting this Rule will impact the TDS section suggested by the tool. Do you want to continue?`}
        confirmText="Proceed"
        itemType="rule"
        type="delete"
        showCancelButton={true}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />

      <ColumnConfiguration
        columns={columns}
        visibleColumns={visibleColumns}
        anchorEl={columnConfigAnchor}
        open={columnConfigOpen}
        onColumnChange={handleColumnChange}
        onClose={handleColumnConfigurationClose}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RuleSetting
